import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './InflationCalculatorPage/components/CalculationForm';
import ResultsOverview from './InflationCalculatorPage/components/ResultsOverview';
import InflationImpactChart from './InflationCalculatorPage/components/InflationImpactChart';
import HistoricalRatesChart from './InflationCalculatorPage/components/HistoricalRatesChart';
import QuickAdjustmentSidebar from './InflationCalculatorPage/components/QuickAdjustmentSidebar';
import CompareScenariosWidget from './InflationCalculatorPage/components/CompareScenariosWidget';
import InfoContent from './InflationCalculatorPage/components/InfoContent';
import PurchasingPowerTable from './InflationCalculatorPage/components/PurchasingPowerTable';

// Определение начального состояния формы
export const initialFormData = {
  amount: '100000',
  startYear: new Date().getFullYear() - 1,
  endYear: new Date().getFullYear(),
  customInflationRate: '',
  showCustomRate: false,
  calculationType: 'depreciation', // depreciation, appreciation, convertValue
  includeChart: true,
  includeHistorical: true,
  compareToBenchmarks: false,
  benchmarks: ['usd', 'gold', 'realEstate'],
  considerTaxes: false,
  incomeTaxRate: '13',
  useAverageInflation: false,
  averageYears: '5',
  selectedRegion: 'russia',
  compoundingPeriods: '1', // Годовое начисление
  itemCategory: 'general', // Категория товаров/услуг
  plannedItems: [] // Планируемые расходы
};

// Данные по инфляции в России (по годам)
export const inflationRatesByYear = {
  2000: 20.2,
  2001: 18.6,
  2002: 15.1,
  2003: 12.0,
  2004: 11.7,
  2005: 10.9,
  2006: 9.0,
  2007: 11.9,
  2008: 13.3,
  2009: 8.8,
  2010: 8.8,
  2011: 6.1,
  2012: 6.6,
  2013: 6.5,
  2014: 11.4,
  2015: 12.9,
  2016: 5.4,
  2017: 2.5,
  2018: 4.3,
  2019: 3.0,
  2020: 4.9,
  2021: 8.4,
  2022: 11.9,
  2023: 7.4,
  2024: 7.8, // Прогнозное значение
  2025: 4.0  // Прогнозное значение
};

// Среднегодовая инфляция по категориям (относительно базовой)
export const categoryInflationMultipliers = {
  general: 1.0, // Общая инфляция
  food: 1.2, // Продукты питания
  housing: 0.9, // Жилье и коммунальные услуги
  transport: 1.1, // Транспорт
  medical: 1.3, // Медицинские услуги
  education: 1.15, // Образование
  clothing: 0.8, // Одежда и обувь
  entertainment: 0.85, // Развлечения
  electronics: 0.7, // Электроника
  furniture: 0.95 // Мебель и бытовая техника
};

// Определение сравнительных активов для инфляции
export const benchmarkAssets = [
  { id: 'usd', name: 'Доллар США', color: 'green' },
  { id: 'eur', name: 'Евро', color: 'blue' },
  { id: 'gold', name: 'Золото', color: 'yellow' },
  { id: 'realEstate', name: 'Недвижимость', color: 'purple' },
  { id: 'stocks', name: 'Фондовый рынок (Индекс МосБиржи)', color: 'red' },
  { id: 'deposits', name: 'Банковские вклады', color: 'teal' }
];

/**
 * InflationCalculatorPage Component - Калькулятор инфляции с различными сценариями и визуализацией
 */
const InflationCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('inflation-calculator') || {
    id: 'inflation-calculator',
    name: 'Калькулятор инфляции',
    metaTitle: 'Калькулятор инфляции в России | Gensio',
    metaDescription: 'Рассчитайте влияние инфляции на ваши сбережения, спланируйте бюджет с учетом роста цен, и сравните различные активы как защиту от инфляции в России.'
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор инфляции в России | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте влияние инфляции на ваши сбережения, спланируйте бюджет с учетом роста цен, и сравните различные активы как защиту от инфляции в России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [historicalData, setHistoricalData] = useState(null);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных сценариев из localStorage ---
  useEffect(() => {
    const savedScenariosData = localStorage.getItem('savedInflationScenarios');
    if (savedScenariosData) {
      try {
        setSavedScenarios(JSON.parse(savedScenariosData));
      } catch (e) {
        console.error("Error loading saved scenarios:", e);
      }
    }
    
    // Загрузка исторических данных
    loadHistoricalData();
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'purchasing-power':
          newState.calculationType = 'depreciation';
          break;
        case 'appreciation':
          newState.calculationType = 'appreciation';
          break;
        case 'future-value':
          newState.calculationType = 'futureValue';
          break;
        case 'savings-planner':
          newState.calculationType = 'savingsPlanner';
          break;
        case 'compare-assets':
          newState.calculationType = 'depreciation';
          newState.compareToBenchmarks = true;
          break;
        case 'historical':
          newState.calculationType = 'depreciation';
          newState.includeHistorical = true;
          newState.startYear = 2000;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
    setShowDetailedAnalysis(false);

  }, [subToolId]);

  // Функция загрузки исторических данных
  const loadHistoricalData = () => {
    // В реальном приложении здесь был бы API-запрос
    // Генерируем данные на основе имеющихся показателей инфляции
    
    const historicalRates = Object.entries(inflationRatesByYear).map(([year, rate]) => ({
      year: parseInt(year),
      inflationRate: rate,
      // Можно добавить другие ключевые показатели
      keyRate: Math.min(rate + 2.5, 17), // Примерная ключевая ставка
      depositRate: Math.min(rate + 1.5, 15), // Примерная ставка по вкладам
      mortgageRate: Math.min(rate + 4, 20), // Примерная ставка по ипотеке
    }));
    
    setHistoricalData({
      rates: historicalRates,
      // Дополнительные агрегированные данные
      aggregated: {
        averageFiveYear: calculateAverageInflation(5),
        averageTenYear: calculateAverageInflation(10),
        maxRate: Math.max(...Object.values(inflationRatesByYear)),
        minRate: Math.min(...Object.values(inflationRatesByYear))
      }
    });
  };

  // Функция расчета средней инфляции за период
  const calculateAverageInflation = (years) => {
    const currentYear = new Date().getFullYear();
    let sum = 0;
    let count = 0;
    
    for (let i = 0; i < years; i++) {
      const year = currentYear - i;
      if (inflationRatesByYear[year]) {
        sum += inflationRatesByYear[year];
        count++;
      }
    }
    
    return count > 0 ? sum / count : 0;
  };

  // Сохранение сценария в localStorage
  const saveScenario = (name) => {
    if (!results) return;

    const newScenario = {
      id: Date.now().toString(),
      name: name || `Инфляция ${formData.startYear}-${formData.endYear}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedScenarios = [...savedScenarios, newScenario];
    setSavedScenarios(updatedScenarios);
    localStorage.setItem('savedInflationScenarios', JSON.stringify(updatedScenarios));

    return newScenario;
  };

  // Удаление сохраненного сценария
  const deleteScenario = (id) => {
    const updatedScenarios = savedScenarios.filter(scenario => scenario.id !== id);
    setSavedScenarios(updatedScenarios);
    localStorage.setItem('savedInflationScenarios', JSON.stringify(updatedScenarios));
  };

  // Загрузка сохраненного сценария
  const loadScenario = (id) => {
    const scenarioToLoad = savedScenarios.find(scenario => scenario.id === id);
    if (scenarioToLoad) {
      setFormData(scenarioToLoad.formData);
      setResults(scenarioToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор инфляции'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              inflationRatesByYear={inflationRatesByYear}
              categoryInflationMultipliers={categoryInflationMultipliers}
              benchmarkAssets={benchmarkAssets}
              saveScenario={saveScenario}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData} 
                  showDetailedAnalysis={showDetailedAnalysis}
                  setShowDetailedAnalysis={setShowDetailedAnalysis}
                />
                
                {formData.includeChart && (
                  <InflationImpactChart 
                    results={results} 
                    formData={formData} 
                  />
                )}
                
                {showDetailedAnalysis && (
                  <PurchasingPowerTable 
                    results={results} 
                    formData={formData} 
                  />
                )}
                
                {formData.includeHistorical && historicalData && (
                  <HistoricalRatesChart 
                    historicalData={historicalData} 
                    selectedYears={{
                      start: formData.startYear,
                      end: formData.endYear
                    }}
                  />
                )}
              </>
            )}

            {/* --- Информационный контент об инфляции --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              inflationRatesByYear={inflationRatesByYear}
            />

            {/* --- Панель сравнения сценариев --- */}
            <CompareScenariosWidget 
              savedScenarios={savedScenarios}
              deleteScenario={deleteScenario}
              loadScenario={loadScenario}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default InflationCalculatorPage;